/**
 * /output-diet — Engine 22 · Output Token Diet.
 *
 * World-first (Jun 2026): every tool optimizes the prompt. Nobody audits the
 * response — yet output tokens cost 3-5× input on every major provider.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Scissors, Sparkles, AlertCircle, RefreshCw, ArrowRight, Layers } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import BrandTitle from '../components/BrandTitle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stat = ({ label, value, sub, color = 'text-white', testId }) => (
  <div className="rounded-xl border border-white/10 bg-slate-900/50 backdrop-blur-xl shadow-sm p-5" data-testid={testId}>
    <div className="text-[10px] font-bold tracking-widest text-slate-500 uppercase mb-1">{label}</div>
    <div className={`text-3xl font-extrabold tabular-nums font-mono ${color}`}>{value}</div>
    {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
  </div>
);

const MODELS = ['gpt-4o', 'gpt-4o-mini', 'claude-sonnet', 'claude-haiku', 'gemini-flash', 'mistral'];

const OutputDiet = () => {
  const [scan, setScan] = useState(null);
  const [scanErr, setScanErr] = useState(false);
  const [loading, setLoading] = useState(true);

  const [sample, setSample] = useState('');
  const [model, setModel] = useState('gpt-4o');
  const [monthlyCalls, setMonthlyCalls] = useState(10000);
  const [result, setResult] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeErr, setAnalyzeErr] = useState('');

  const auth = () => ({ 'Authorization': `Bearer ${localStorage.getItem('token') || ''}`, 'Content-Type': 'application/json' });

  const fetchScan = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/output-diet/scan?days=30`, { headers: auth() });
      if (!r.ok) throw new Error('unauthorized');
      setScan(await r.json());
      setScanErr(false);
    } catch (e) {
      setScanErr(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchScan(); /* eslint-disable-next-line */ }, []);

  const analyze = async () => {
    if (sample.trim().length < 20) { setAnalyzeErr('Paste at least 20 characters of a real LLM response.'); return; }
    setAnalyzing(true); setAnalyzeErr('');
    try {
      const r = await fetch(`${API}/output-diet/analyze`, {
        method: 'POST', headers: auth(),
        body: JSON.stringify({ response_text: sample, model, monthly_calls: Number(monthlyCalls) || 1 }),
      });
      if (!r.ok) throw new Error((await r.json())?.detail || 'Analysis failed — sign in to use the analyzer.');
      setResult(await r.json());
    } catch (e) {
      setAnalyzeErr(e.message || 'Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="output-diet-page">
      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="mb-8">
          <Badge className="bg-rose-500/10 border-rose-400/40 text-rose-300 px-3 py-1 text-[11px] tracking-[0.15em] uppercase font-semibold mb-3" data-testid="od-badge">
            <Scissors className="w-3 h-3 mr-1.5 inline" /> Engine 22 · Output Token Diet · world-first
          </Badge>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight"><BrandTitle>Output Token Diet</BrandTitle></h1>
          <p className="text-slate-300 mt-2 max-w-2xl">
            Everyone optimizes the prompt. Nobody optimizes the <em>response</em> — yet output tokens cost
            <span className="text-rose-300 font-semibold"> 3-5× more than input</span> on every major provider.
            This engine finds the verbosity, boilerplate, and prose-wrapped JSON you pay premium rates for.
          </p>
        </div>

        {/* Paste analyzer */}
        <Card className="bg-slate-900/50 backdrop-blur-xl border-white/10 mb-10">
          <CardContent className="p-6">
            <h2 className="text-base font-bold text-white mb-1 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-rose-400" /> Paste a real response — get the diet plan
            </h2>
            <p className="text-xs text-slate-500 mb-4">Deterministic analysis. Nothing is stored, no LLM is called.</p>
            <textarea
              value={sample}
              onChange={(e) => setSample(e.target.value)}
              placeholder={'Paste an LLM response here…\n\ne.g. "Certainly! I\'d be happy to help. Here\'s the summary you asked for… I hope this helps! Let me know if you have any other questions."'}
              className="w-full h-36 rounded-lg border border-white/15 bg-slate-950 px-3 py-2 text-sm font-mono text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-rose-400/50"
              data-testid="od-sample-input"
            />
            <div className="flex flex-wrap items-center gap-3 mt-3">
              <select value={model} onChange={(e) => setModel(e.target.value)} className="rounded-lg border border-white/15 px-3 py-1.5 text-sm bg-slate-900" data-testid="od-model-select">
                {MODELS.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
              <label className="text-xs text-slate-400 flex items-center gap-2">
                Monthly calls
                <input type="number" value={monthlyCalls} onChange={(e) => setMonthlyCalls(e.target.value)} min="1"
                  className="w-28 rounded-lg border border-white/15 px-2 py-1.5 text-sm bg-slate-900 tabular-nums" data-testid="od-monthly-calls" />
              </label>
              <button onClick={analyze} disabled={analyzing}
                className="inline-flex items-center gap-1.5 rounded-lg bg-rose-500 hover:bg-rose-600 disabled:opacity-50 text-white px-4 py-1.5 text-sm font-semibold"
                data-testid="od-analyze-btn">
                {analyzing ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Scissors className="w-3.5 h-3.5" />} Analyze
              </button>
              {analyzeErr && <span className="text-xs text-rose-400" data-testid="od-analyze-error">{analyzeErr}</span>}
            </div>

            {result && (
              <div className="mt-6 rounded-xl border border-rose-400/20 bg-rose-500/5 p-5" data-testid="od-result">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <Stat label="Waste %" value={`${result.waste_pct}%`} color="text-rose-300" sub={`${result.waste_tokens_est} of ${result.total_tokens_est} tokens`} testId="od-result-waste-pct" />
                  <Stat label="Boilerplate" value={result.boilerplate_tokens} sub="openers · closers · hedges" testId="od-result-boilerplate" />
                  <Stat label="JSON wrap tax" value={result.json_wrap_tokens} sub="prose around structured output" testId="od-result-jsonwrap" />
                  <Stat label="Monthly savings" value={`$${result.projected_monthly_savings_usd.toLocaleString()}`} color="text-emerald-300" sub={`at ${Number(monthlyCalls).toLocaleString()} calls/mo`} testId="od-result-savings" />
                </div>
                <div className="text-sm text-slate-200 space-y-1.5" data-testid="od-result-recs">
                  <div><span className="text-slate-500 font-mono text-xs">max_tokens →</span> <span className="font-semibold text-white">{result.recommendations.suggested_max_tokens}</span></div>
                  {result.recommendations.stop_sequences.length > 0 && (
                    <div><span className="text-slate-500 font-mono text-xs">stop →</span> <span className="font-mono text-xs text-amber-300">{JSON.stringify(result.recommendations.stop_sequences)}</span></div>
                  )}
                  <div><span className="text-slate-500 font-mono text-xs">system →</span> <span className="italic text-slate-300">"{result.recommendations.system_directive}"</span></div>
                  {result.recommendations.use_json_mode && <div className="text-amber-300 text-xs">→ Switch this call to JSON mode / structured output — you're paying for explanation prose around JSON.</div>}
                </div>
                {result.findings.length > 0 && (
                  <details className="mt-3">
                    <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-300">Show {result.findings.length} matched phrases</summary>
                    <ul className="mt-2 space-y-1">
                      {result.findings.map((f, i) => (
                        <li key={i} className="text-xs font-mono text-slate-400"><span className="text-rose-400">[{f.kind}]</span> "{f.match}" <span className="text-slate-600">~{f.est_tokens} tok</span></li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Authed scan */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-bold text-white">Your live output-waste scan (last 30 days)</h2>
          <button onClick={fetchScan} className="inline-flex items-center gap-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 px-3 py-1.5 text-sm font-semibold" data-testid="od-scan-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
        {scanErr ? (
          <div className="rounded-xl border border-white/10 bg-slate-900/50 backdrop-blur-xl px-6 py-10 text-center text-slate-500" data-testid="od-scan-unauth">
            <AlertCircle className="w-8 h-8 mx-auto text-slate-400 mb-2" />
            Sign in and route traffic through the <Link to="/engine-27" className="text-emerald-400 hover:underline">Auto-Compiler Proxy</Link> to see your real output-waste numbers.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
              <Stat label="Output / Input ratio" value={scan?.overall_output_input_ratio ?? '—'} sub="tokens out per token in" testId="od-stat-ratio" />
              <Stat label="Long-tail waste" value={scan?.est_long_tail_waste_usd != null ? `$${scan.est_long_tail_waste_usd.toLocaleString()}` : '—'} color="text-rose-300" sub="responses >1.5× median length" testId="od-stat-waste" />
              <Stat label="Models scanned" value={scan?.models_scanned ?? '—'} sub={`${(scan?.total_output_tokens || 0).toLocaleString()} output tokens`} testId="od-stat-models" />
            </div>
            <div className="rounded-2xl bg-slate-900/60 border border-white/10 overflow-hidden mb-2">
              {(!scan?.rows || scan.rows.length === 0) ? (
                <div className="px-6 py-10 text-center text-slate-500" data-testid="od-scan-empty">
                  <AlertCircle className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                  No proxy traffic yet. Point your base_url at the Auto-Compiler Proxy and this fills itself.
                </div>
              ) : (
                <table className="w-full text-sm" data-testid="od-scan-table">
                  <thead className="bg-slate-900/60 border-b border-white/10">
                    <tr>
                      <th className="text-left px-6 py-3 font-semibold text-slate-300">Model</th>
                      <th className="text-right px-3 py-3 font-semibold text-slate-300">Calls</th>
                      <th className="text-right px-3 py-3 font-semibold text-slate-300">Out/In</th>
                      <th className="text-right px-3 py-3 font-semibold text-slate-300">Median out</th>
                      <th className="text-right px-6 py-3 font-semibold text-slate-300">Waste $</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/10">
                    {scan.rows.map((r, i) => (
                      <tr key={r.model} className="hover:bg-slate-900/60" data-testid={`od-scan-row-${i}`}>
                        <td className="px-6 py-3 font-mono text-emerald-300">{r.model}</td>
                        <td className="px-3 py-3 text-right tabular-nums text-slate-200">{r.calls}</td>
                        <td className="px-3 py-3 text-right tabular-nums text-slate-200">{r.output_input_ratio ?? '—'}</td>
                        <td className="px-3 py-3 text-right tabular-nums text-slate-200">{r.median_output_tokens}</td>
                        <td className="px-6 py-3 text-right tabular-nums font-semibold text-rose-300">${r.est_waste_usd}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

        {/* Educational footer */}
        <Card className="bg-slate-900/50 backdrop-blur-xl border-white/10 mt-8">
          <CardContent className="p-5">
            <h3 className="font-bold text-white flex items-center gap-2 mb-2"><Layers className="w-4 h-4 text-rose-400" /> Why the output side matters</h3>
            <p className="text-sm text-slate-200 leading-relaxed">
              GPT-4o charges $2.50/1M input but <strong>$10/1M output</strong>. Claude Sonnet: $3 in, <strong>$15 out</strong>.
              A response that opens with "Certainly! I'd be happy to help" and closes with "Let me know if you have any other
              questions" carries ~30-60 tokens of pure boilerplate — at premium output rates, on <em>every single call</em>.
              Multiply by millions of calls and the closing pleasantries alone are a real line item.
            </p>
            <p className="text-xs text-slate-500 mt-2 italic">
              First-to-market: as of June 2026 no other FinOps or LLM-observability tool audits response-side waste.
            </p>
            <Link to="/memory-compactor" className="inline-flex items-center gap-1.5 text-sm text-emerald-400 hover:text-emerald-300 mt-3 font-semibold" data-testid="od-link-memory-compactor">
              Next: kill the chat-history resend tax <ArrowRight className="w-4 h-4" />
            </Link>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default OutputDiet;
